// QUANTOS PERFIS DE ARTISTA ESTA CONTA PODE TER.
//
// ⚠️ A REGRA VIVE EM TRÊS LUGARES, e é de propósito — mas os três têm de dizer o mesmo:
//
//   1. `public.fn_cota_de_perfis` (gatilho em `artists`) — a rede final, que vale inclusive para
//      o app nativo e para quem chamar a API direto;
//   2. `supabase/functions/artist-diagnostic` — barra ANTES de rodar o diagnóstico inteiro, com
//      mensagem melhor;
//   3. esta função — para a interface não deixar a pessoa ENTRAR num funil que vai ser recusado
//      no fim. Era o que acontecia: criava o perfil, respondia o quiz todo e só então levava 402.
//
// Este arquivo é a definição de referência, e é a única das três com teste. Mudou aqui, mudou nos
// outros dois: um limite que diverge entre a tela e o banco recusa a pessoa depois de ela
// trabalhar, que é a pior forma de recusar.

export interface ComposicaoDaCotaDePerfis {
  /** Assinatura ativa, ou atrasada dentro da carência. */
  isPro: boolean;
  /** `included_profiles` da config do plano. */
  inclusos: number;
  /** `extra_profiles` contratados como add-on. */
  extras: number;
}

/**
 * O teto de perfis que DISPUTAM a cota.
 *
 * ⚠️ O GRÁTIS TEM UM, e não zero. `useEntitlements.profileSlots` devolve 0 para quem não assina,
 * porque lá a pergunta é "quantos perfis a ASSINATURA cobre" — e sem assinatura, nenhum. Aqui a
 * pergunta é outra: "quantos perfis a conta pode ter", e a resposta do plano grátis é um. Usar
 * `profileSlots` para esta decisão bloquearia a criação do primeiro perfil de todo mundo.
 *
 * Perfis LEGADO (comprados no modelo antigo) ficam fora desta conta nos três lugares: quem pagou
 * por eles não perde a vaga que já tinha, nem consome a da assinatura.
 */
export function limiteDePerfis({ isPro, inclusos, extras }: ComposicaoDaCotaDePerfis): number {
  if (!isPro) return 1;
  return Math.max(1, Math.trunc(inclusos) + Math.max(0, Math.trunc(extras)));
}

/** Ainda cabe mais um perfil? `naCota` é quantos a conta já tem, sem contar os legados. */
export function cabeMaisUmPerfil(
  composicao: ComposicaoDaCotaDePerfis & { naCota: number },
): boolean {
  return composicao.naCota < limiteDePerfis(composicao);
}

// ── QUAIS PERFIS FICAM ATIVOS (QA de pagamentos, C5; decisão do Lucas, 05/10/2026) ────────────
//
// Quando a cota cai (a pessoa reduz o plano, ou a assinatura acaba), os perfis que passam dela
// ficam suspensos: travados, nunca apagados, e voltam quando a cota sobe. Quem decide é o banco
// (`alinhar_perfis`, migração 20261005030000), sempre pela mesma ordem: primeiro os da escolha da
// conta (o mais recente antes), depois os que já estão ativos, depois os mais antigos. Os ativos
// vêm antes dos travados para o alinhamento nunca trocar um pelo outro sozinho: um travado só volta
// quando a cota sobe (não há troca entre os perfis da conta). Esta é a mesma ordem, para a tela
// mostrar quais ficam. Mudou lá, muda aqui.

export interface PerfilNaCota {
  id: string;
  created_at?: string | null;
  /** Quando a pessoa escolheu manter este perfil (nula: sem escolha). */
  fica_na_cota?: string | null;
  slot_state?: 'active' | 'suspended' | null;
  legacy_unlocked?: boolean | null;
}

/** Os perfis que disputam a cota (fora os legados), na ordem do banco. */
export function ordemDaCota<T extends PerfilNaCota>(perfis: T[]): T[] {
  const tempo = (v?: string | null) => (v ? new Date(v).getTime() : 0);
  return perfis
    .filter((p) => !p.legacy_unlocked)
    .slice()
    .sort((a, b) => {
      const ea = a.fica_na_cota ? 1 : 0;
      const eb = b.fica_na_cota ? 1 : 0;
      if (ea !== eb) return eb - ea;
      if (ea && eb) return tempo(b.fica_na_cota) - tempo(a.fica_na_cota);
      const aa = a.slot_state === 'suspended' ? 1 : 0;
      const ab = b.slot_state === 'suspended' ? 1 : 0;
      if (aa !== ab) return aa - ab;
      const porCriacao = tempo(a.created_at) - tempo(b.created_at);
      return porCriacao !== 0 ? porCriacao : a.id.localeCompare(b.id);
    });
}

/** Os ids que ficam ativos com este limite, pela ordem do banco. */
export function quaisFicam(perfis: PerfilNaCota[], limite: number): string[] {
  return ordemDaCota(perfis).slice(0, Math.max(0, limite)).map((p) => p.id);
}
